import React, { useEffect, useState } from 'react';
import { getShows, getFeed, getVideoEpisodes } from '../services/contentService';
import { Show, Episode, ContentType, MediaType } from '../types';
import { useMedia } from '../context/MediaContext';
import { Play, Video as VideoIcon, Clock, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

export const VideoHub: React.FC = () => {
  const [videos, setVideos] = useState<Episode[]>([]);
  const [videoShows, setVideoShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const { play } = useMedia();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [episodes, shows] = await Promise.all([
        getVideoEpisodes(),
        getShows('video')
      ]);
      setVideos(episodes);
      setVideoShows(shows);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div></div>;
  }

  const featuredVideo = videos[0];
  const otherVideos = videos.slice(1);

  return (
    <div className="animate-fade-in pb-20">
      <SEOHead
        title="Video Hub"
        description="Watch award-winning documentaries and long-form visual investigative reports from Nida Studios."
      />
      <div className="flex items-center gap-3 mb-10">
        <div className="p-3 bg-brand-100 rounded-full text-brand-400">
          <VideoIcon size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-sans font-black text-brand-900 uppercase tracking-tight">Nida Video</h1>
          <p className="text-gray-500 text-xs font-medium">Visual deep dives and immersive reporting.</p>
        </div>
      </div>

      {/* Hero Video */}
      {featuredVideo && (
        <section className="mb-16">
          <div
            className="group relative aspect-video w-full rounded-2xl overflow-hidden shadow-lg cursor-pointer bg-black max-h-[480px]"
            onClick={() => play(featuredVideo)}
          >
            <img
              src={featuredVideo.thumbnail}
              alt={featuredVideo.title}
              className="w-full h-full object-cover opacity-70 group-hover:opacity-60 transition-opacity duration-700"
            />
            <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
              <span className="text-brand-400 font-bold text-xs uppercase tracking-widest mb-3">Featured Release</span>
              <h2 className="text-2xl md:text-2xl font-serif font-bold text-white mb-4 group-hover:text-brand-400 transition-colors">
                {featuredVideo.title}
              </h2>
              <p className="text-gray-300 text-base md:w-2/3 line-clamp-2 mb-6 font-serif italic">
                {featuredVideo.description}
              </p>
              <div className="flex items-center gap-6">
                <button className="bg-white text-brand-900 px-6 py-2 rounded-full font-bold flex items-center gap-2 hover:bg-brand-400 hover:text-white hover:scale-105 transition-all">
                  <Play size={18} fill="currentColor" /> Watch Now
                </button>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock size={16} /> {Math.floor(featuredVideo.duration / 60)} min
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Video Shows Carousel */}
      {videoShows.length > 0 && (
        <section className="mb-16">
          <h3 className="text-lg font-bold text-brand-900 uppercase tracking-widest mb-8 border-b border-gray-100 pb-4">Video Series</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videoShows.map(show => (
              <Link to={`/video/show/${show.slug || show.id}`} key={show.id} className="group cursor-pointer">
                <div className="aspect-[4/3] rounded-xl overflow-hidden mb-3 shadow-md relative">
                  <img src={show.coverImage} alt={show.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-brand-900/10 group-hover:bg-transparent transition-colors" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 leading-tight group-hover:text-brand-400 transition-colors">{show.title}</h3>
                <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-widest">{show.category}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Video Grid */}
      <section>
        <h3 className="text-lg font-bold text-brand-900 uppercase tracking-widest mb-8 border-b border-gray-100 pb-4">Recent Documentaries</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherVideos.map(video => (
            <div
              key={video.id}
              className="group cursor-pointer"
              onClick={() => play(video)}
            >
              <div className="relative aspect-video rounded-xl overflow-hidden shadow-md bg-gray-100 mb-4">
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
                    <Play size={20} className="fill-brand-900 text-brand-900 ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded backdrop-blur-sm">
                  {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                </div>
              </div>
              <h4 className="text-base font-bold text-brand-900 leading-tight mb-2 group-hover:text-brand-400 transition-colors">
                {video.title}
              </h4>
              <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                {video.description}
              </p>
              <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                <Calendar size={12} /> {new Date(video.publishedAt).toLocaleDateString()}
              </div>
            </div>
          ))}
          {otherVideos.length === 0 && !featuredVideo && (
            <div className="col-span-full py-12 text-center text-gray-400 font-serif italic">
              More long-form content arriving weekly.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};